import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './Button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught React exception:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  private handleGoHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-secondary p-4">
          <div className="max-w-md w-full bg-surface border border-border rounded-3xl p-6 shadow-xl space-y-5 text-center">
            <div className="w-14 h-14 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-500 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-7 h-7" />
            </div>

            <div>
              <h2 className="text-xl font-extrabold text-text-primary tracking-tight">
                Algo no salió como se esperaba
              </h2>
              <p className="text-xs text-text-secondary mt-1.5 leading-relaxed">
                Se detectó una interrupción temporal en la interfaz. No te preocupes, tus datos están seguros y guardados en tu base de datos local.
              </p>
            </div>

            {this.state.error && (
              <div className="p-3 rounded-xl bg-surface-tertiary/60 border border-border text-left overflow-hidden">
                <span className="text-[10px] font-bold text-text-tertiary uppercase tracking-wider block mb-1">
                  Detalle técnico del error:
                </span>
                <p className="text-[11px] font-mono text-rose-600 dark:text-rose-400 break-words line-clamp-3">
                  {this.state.error.message || 'Excepción no controlada'}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center gap-2 pt-2">
              <Button
                variant="primary"
                size="md"
                fullWidth
                icon={RefreshCw}
                onClick={this.handleReset}
              >
                Recargar Aplicación
              </Button>

              <Button
                variant="secondary"
                size="md"
                fullWidth
                icon={Home}
                onClick={this.handleGoHome}
              >
                Ir al Inicio
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
